import React, { useEffect, useState } from 'react';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { red, green, yellow } from '@material-ui/core/colors';
import { IStory, ITheme, IWriter } from '../../services/types';
import { searchByKeyword } from '../../services/api/Search';

interface IProps {}

interface IQueryParams {
  keyword?: string;
}

interface ISearchResult {
  id?: number;
  type: string;
  story?: IStory;
  writer?: IWriter;
  theme?: ITheme;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(3),
      overflowX: 'auto',
    },
    table: {
      minWidth: 650,
    },
    story: {
      backgroundColor: yellow[50],
    },
    user: {
      backgroundColor: green[50],
    },
    theme: {
      backgroundColor: red[50],
    },
  }),
);

const Search = (): JSX.Element => {
  const classes = useStyles();
  const location = useLocation();

  const [searchResults, setSearchResults] = useState<ISearchResult[]>();

  useEffect(() => {
    const { keyword } = queryString.parse(location.search) as IQueryParams;

    const fetchData = async () => {
      if (keyword) {
        try {
          const response = await searchByKeyword(keyword);
          const result: ISearchResult[] = response.data.items;

          setSearchResults(result);
        } catch (e) {
          console.error('error', e); // FIXME: Proper error handling.
        }
      }
    };

    fetchData();
  }, [location]);

  return (
    <Paper className={classes.root}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Writer</TableCell>
            <TableCell>Views</TableCell>
            <TableCell>Main Themes</TableCell>
            <TableCell>Detailed Themes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {searchResults?.map(row => {
            if (!row.type) {
              return;
            }

            if ((row.type === 'story' || row.type === 'season') && row.story) {
              const story = row.story;

              return (
                <TableRow key={`story-${row.type}-${row.id}`} className={classes.story}>
                  <TableCell component="th" scope="row">
                    {row.type}
                  </TableCell>
                  <TableCell>{story.title}</TableCell>
                  <TableCell>{story.pennames.map(p => p.name).join(', ')}</TableCell>
                  <TableCell>{story.viewCount}</TableCell>
                  <TableCell>{story?.themes?.main?.map(t => t.title).join(', ')}</TableCell>
                  <TableCell>{story?.themes?.detailed?.map(t => t.title).join(', ')}</TableCell>
                </TableRow>
              );
            } else if (row.type === 'writer' && row.writer) {
              const writer = row.writer;

              return (
                <TableRow key={`user-${writer.id}`} className={classes.user}>
                  <TableCell component="th" scope="row">
                    {row.type}
                  </TableCell>
                  <TableCell colSpan={6}>
                    <TableCell>{writer.licensedPennames.map(p => p.name).join(', ')}</TableCell>
                  </TableCell>
                </TableRow>
              );
            } else if (row.type === 'theme' && row.theme) {
              const theme = row.theme;

              return (
                <TableRow key={`theme-${theme.id}`} className={classes.theme}>
                  <TableCell component="th" scope="row">
                    {row.type}
                  </TableCell>
                  <TableCell colSpan={6}>{theme.title}</TableCell>
                </TableRow>
              );
            }
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default Search;
